<template>
    <div class="frame">
        <div class="panel">
            <ContextRings :dimensions="dimensions" :percent="percent" :size="size" />

            <ul class="dimensions">
                <li v-for="dimension in dimensions" :key="dimension.id" class="dimension" :style="{ '--dimension-color': ringPalette(dimension.id).dot }">
                    <div class="dimension-head">
                        <span aria-hidden="true" class="dimension-dot"></span>
                        <h3 class="dimension-label">{{ dimension.label }}</h3>
                        <!-- Omit the projected-value arrow here; the action line below already shows that
                             value.
                             -->
                        <span class="dimension-figure">{{ formatNumber(dimension.value, "percent") }}</span>
                        <span class="sr-only">{{ dimension.state }}</span>
                    </div>
                    <p class="dimension-body">{{ dimension.body }}</p>
                    <p v-if="dimension.step" class="dimension-next">
                        {{ t("onboarding.profileSetup.panel.reaches", { action: dimension.action, value: formatNumber(dimension.potential, "percent") }) }}
                    </p>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ringPalette } from "~vue/ProfileSetup/contextRingColors";
import ContextRings from "~vue/ProfileSetup/ContextRings.vue";
import type { ContextDimension } from "~vue/ProfileSetup/types";
import { formatNumber } from "~vue/utils/formatNumber";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineProps<{ dimensions: ContextDimension[]; percent: number; size: number }>();
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .frame {
        container-type: inline-size;
    }

    .panel {
        @apply flex flex-col items-start gap-4;
    }

    .dimensions {
        @apply flex w-full min-w-0 flex-col gap-2;
    }

    /*
     * These cards have no actions, so avoid hover effects that imply interactivity. The dots identify their
     * corresponding rings.
     */
    .dimension {
        @apply rounded-lgr border border-border bg-surface-raised px-4 py-4;
    }

    .dimension-head {
        @apply flex flex-wrap items-center gap-x-2.5 gap-y-1;
    }

    .dimension-dot {
        @apply size-2.5 shrink-0 rounded-full;
        background-color: var(--dimension-color);
    }

    .dimension-label {
        @apply body-bold min-w-0 flex-1 text-ink;
    }

    .dimension-figure {
        @apply scaffold-body-small-medium ml-auto shrink-0 pl-6 text-right text-ink-muted tabular-nums;
    }

    .dimension-body {
        @apply details-big mt-1 max-w-[64ch] text-ink-muted;
    }

    /*
     * Keep the projected value muted; coloring it made noninteractive text look like a link. The label dot
     * already identifies the dimension.
     */
    .dimension-next {
        @apply details mt-2 text-ink-muted italic;
    }

    /*
     * Keep the breakpoint below the host step's 672px width so the unlabeled chart and its legend can
     * appear side by side.
     */
    @container (min-width: 520px) {
        .panel {
            @apply flex-row items-center gap-6;
        }

        .dimensions {
            @apply flex-1;
        }
    }
}
</style>
